"use client";

import { useLocale, useTranslations } from "next-intl";
import { BatteryCharging, Package, Smartphone } from "lucide-react";
import { formatBikeSpecs, getBikeEquipment, type BikeEquipmentKey } from "@/lib/mobility/bike-specs";
import {
  formatModelSpecRows,
  mergeModelSpecRows,
  type ModelSpecFacts,
} from "@/lib/mobility/model-spec-format";
import FactTable, { type FactTableRow } from "./FactTable";

interface BikeSpecTableProps {
  /** `products.specifications` 원본 — `kind !== "bike"` 면 아무것도 그리지 않는다. */
  specifications?: Record<string, unknown> | null;
  /**
   * 이 상품만의 사실(필요 면허·주행거리·수령 방식·편도·지점 수 — `lib/product-facts`)을
   * 같은 표 아래쪽 행으로 붙인다. 표를 하나 더 두지 않기 위해서다.
   */
  extraRows?: readonly FactTableRow[];
  /**
   * 제조사 공식 제원(`lib/mobility/model-specs`, 서버가 표시 규칙을 적용해 넘긴다) —
   * 변속 행에 단수·DCT 를 더하고 배기량·엔진 형식(확인된 모델은 시트고·중량·탱크·연비)
   * 행을 같은 표에 붙인다.
   */
  modelSpecs?: ModelSpecFacts | null;
  /** "차종" 값 — 모델 허브의 그 언어 이름(+ 사양). 없으면 원문 specifications.model */
  modelName?: string | null;
}

/**
 * 차량 정보 표 — 오토바이 상품에서만 나온다.
 *
 * 데스크톱은 라벨/값 2쌍씩, 모바일은 1쌍씩(`FactTable`). 차량 제원 포맷은
 * `lib/mobility/bike-specs.ts`, 덧붙는 사실 행은 `lib/product-facts/key-facts.ts` 가 맡는다.
 */
export default function BikeSpecTable({
  specifications,
  extraRows = [],
  modelSpecs = null,
  modelName = null,
}: BikeSpecTableProps) {
  const t = useTranslations("product.bikeSpecs");
  const locale = useLocale();

  const baseRows = formatBikeSpecs(specifications, t, locale, modelName);
  const equipment = getBikeEquipment(specifications);
  if (baseRows.length === 0) return null;
  const productTransmission =
    typeof specifications?.transmission === "string" ? specifications.transmission : null;
  const rows = mergeModelSpecRows(
    baseRows,
    formatModelSpecRows(modelSpecs, productTransmission, t, locale),
  );

  const equipmentItems = [
    { key: "smartphoneHolder", Icon: Smartphone },
    { key: "usbCharger", Icon: BatteryCharging },
    { key: "topBox", Icon: Package },
  ] as const satisfies ReadonlyArray<{ key: BikeEquipmentKey; Icon: typeof Smartphone }>;

  return (
    <div>
      <h2 className="text-lg sm:text-xl font-semibold mb-4">{t("title")}</h2>

      <FactTable rows={[...rows, ...extraRows]} />
      {equipment && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">{t("equipment.title")}</h3>
          <div className="flex flex-wrap gap-2">
            {equipmentItems.map(({ key, Icon }) => {
              const status = equipment[key];
              return (
                <span
                  key={key}
                  className={`inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold sm:text-sm ${status === "provided" ? "border-orange-600 bg-orange-50 text-orange-800" : "border-gray-300 bg-gray-100 text-gray-600"}`}
                  aria-label={`${t(`equipment.${key}`)}: ${t(`equipment.status.${status}`)}`}
                  title={t(`equipment.status.${status}`)}
                >
                  <Icon aria-hidden="true" size={15} strokeWidth={2} />
                  {t(`equipment.${key}`)}
                  {status === "varies" || status === "unverified" ? ` · ${t(`equipment.status.${status}`)}` : null}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
